// ExportarProdutos.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Select,
  Row,
  Typography,
  Input,
  Modal,
  Form,
  Switch,
  notification,
} from "antd";
import styled from "styled-components";
import axios from "axios";
import * as XLSX from "xlsx";

const { Option } = Select;
const { Title } = Typography;

/* =========================
   ESTILOS
========================= */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  min-height: 100vh;
  background: #f0f2f5;
`;

const FilterCard = styled(Card)`
  width: 600px;
  margin-top: 50px;
  padding: 24px;
`;

const FilterRow = styled(Row)`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** Switch com fundo preto */
const BlackSwitch = styled(Switch)`
  .ant-switch-checked {
    background-color: black !important;
  }
  .ant-switch {
    background-color: black !important;
  }
`;

/* =========================
   COLUNAS (API key -> Header/UI)
   - dataIndex/key = campo vindo da API
   - header = rótulo exibido (tabela/planilha)
========================= */
const COLS = [
  { key: "id", header: "id" },
  { key: "nome", header: "nome" },
  { key: "descricao", header: "descricao" },
  { key: "preco", header: "preco" },
  { key: "custo", header: "custo" },
  { key: "codigo", header: "codigo" },
  { key: "ean", header: "ean" },
  { key: "unidade", header: "unidade" },

  // Apresentar o nome da categoria na planilha:
  { key: "categoria", header: "nome da categoria" },

  { key: "ncm", header: "ncm" },
  { key: "cfop", header: "cfop" },
  { key: "origem", header: "origem" },
  { key: "cst", header: "cst" },

  // EXEMPLO PEDIDO: recebe picms -> mostra aliquota_icms
  { key: "picms", header: "aliquota_icms" },

  { key: "predBc", header: "reducao_icms" },
  { key: "cstIpi", header: "cstIpi" },
  { key: "pipi", header: "aliquota_ipi" },
  { key: "cstPis", header: "cstPis" },
  { key: "ppis", header: "aliquota_pis" },
  { key: "cstCofins", header: "cstCofins" },
  { key: "pcofins", header: "aliquota_cofins" },
];

/* =========================
   UTILS
========================= */
function parseNumberBR(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number") return value;
  const s = String(value).replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isNaN(n) ? value : n;
}

/** Monta o Worksheet a partir de uma lista de objetos NORMALIZADOS */
function montarWorksheetAoA(rows) {
  const headerRow = COLS.map((c) => c.header);
  const aoa = [headerRow];

  for (const obj of rows) {
    aoa.push(COLS.map((c) => (obj?.[c.key] ?? "")));
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = COLS.map((c) => ({
    wch: Math.min(Math.max((c.header || "").length + 2, 12), 30),
  }));
  return ws;
}

/* =========================
   COMPONENTE
========================= */
const ExportarProdutos = ({ handleCloseModal }) => {
  const [form] = Form.useForm();

  // Filtros e ordenação
  const [nameFilter, setNameFilter] = useState("");
  const [nameOrder, setNameOrder] = useState("Nenhum");
  const [nameAsc, setNameAsc] = useState(true);

  const [categoryFilter, setCategoryFilter] = useState("todas");
  const [categoryOrder, setCategoryOrder] = useState("Nenhum");
  const [categoryAsc, setCategoryAsc] = useState(true);

  const [stockMin, setStockMin] = useState(0);
  const [stockMax, setStockMax] = useState(99999);
  const [stockOrder, setStockOrder] = useState("Nenhum");
  const [stockAsc, setStockAsc] = useState(true);

  const accessToken = window.localStorage.getItem("accessToken");
  const idUser = window.localStorage.getItem("idUser");

  const [loja, setLoja] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [transactionId, setTransactionId] = useState("");
  const [executarBusca, setExecutarBusca] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transacaoPendente, setTransacaoPendente] = useState(true);

  const containerRef = useRef(null);

  /* ========= Buscar loja do usuário ========= */
  useEffect(() => {
    if (!accessToken || !idUser) return;

    const options = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    };

    axios
      .get(
        `https://techfood-api.herokuapp.com/v1/restaurantes/usuario/${idUser}`,
        options
      )
      .then((res) => setLoja(res.data))
      .catch(() => {
        notification.error({
          message: "Erro",
          description: "Não foi possível carregar a loja do usuário.",
        });
      });
  }, [idUser, accessToken]);

  /* ========= Buscar categorias da loja ========= */
  useEffect(() => {
    if (!loja?.id || !accessToken) return;

    const options = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
    };

    axios
      .get(
        `https://techspdv-api-d3e739785e34.herokuapp.com/v1/categorias/restaurante/${loja.id}`,
        options
      )
      .then((res) => setCategorias(res.data || []))
      .catch(() => {
        setCategorias([]);
      });
  }, [loja?.id, accessToken]);

  /* ========= (Opcional) Monitoramento de transação pendente ========= */
  useEffect(() => {
    async function fetchData() {
      if (!transacaoPendente || !loja?.id || !accessToken) return;

      let cont = 0;
      const options = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      };

      try {
        const respAux = await axios.get(
          `https://techspdv-api-d3e739785e34.herokuapp.com/v1/movimentacaoEstoque/getTransacaoPendenteManutencaoProdutoPorLojaeUsuario/${loja.id}`,
          options
        );
        let resp = respAux.data;
        if (Array.isArray(resp) && resp.length === 0) {
          setTransacaoPendente(false);
          return;
        } else {
          window.alert("Existem alterações sendo processadas.");
        }

        while (cont < 6) {
          cont++;
          const r = await axios.get(
            `https://techspdv-api-d3e739785e34.herokuapp.com/v1/movimentacaoEstoque/getTransacaoPendenteManutencaoProdutoPorLojaeUsuario/${loja.id}`,
            options
          );
          resp = r.data;
          if (Array.isArray(resp) && resp.length === 0) {
            setTransacaoPendente(false);
            break;
          } else {
            notification.info({
              message: "Informação",
              description: "Existem alterações sendo processadas.",
            });
          }
          await new Promise((resolve) => setTimeout(resolve, 20000));
        }
      } catch (e) {
        // silencioso
      }
    }
    fetchData();
  }, [loja?.id, transacaoPendente, accessToken]);

  /* ========= Resetar filtros ========= */
  const resetFilters = () => {
    setNameFilter("");
    setNameOrder("Nenhum");
    setNameAsc(true);

    setCategoryFilter("todas");
    setCategoryOrder("Nenhum");
    setCategoryAsc(true);

    setStockMin(0);
    setStockMax(99999);
    setStockOrder("Nenhum");
    setStockAsc(true);
  };

  /* ========= Montar URL e buscar ========= */
  const handleContinue = () => {
    setLoading(true);

    let filtros = "?";
    if (nameOrder === "Primeiro") {
      filtros += `ord1=nome&ord1Direction=${nameAsc ? "asc" : "desc"}`;
    } else if (categoryOrder === "Primeiro") {
      filtros += `ord1=categoria_id&ord1Direction=${categoryAsc ? "asc" : "desc"}`;
    } else if (stockOrder === "Primeiro") {
      filtros += `ord1=quantidade_estoque&ord1Direction=${stockAsc ? "asc" : "desc"}`;
    }

    if (nameOrder === "Segundo") {
      filtros += `&ord2=nome&ord2Direction=${nameAsc ? "asc" : "desc"}`;
    } else if (categoryOrder === "Segundo") {
      filtros += `&ord2=categoria_id&ord2Direction=${categoryAsc ? "asc" : "desc"}`;
    } else if (stockOrder === "Segundo") {
      filtros += `&ord2=quantidade_estoque&ord2Direction=${stockAsc ? "asc" : "desc"}`;
    }

    if (nameOrder === "Terceiro") {
      filtros += `&ord3=nome&ord3Direction=${nameAsc ? "asc" : "desc"}`;
    } else if (categoryOrder === "Terceiro") {
      filtros += `&ord3=categoria_id&ord3Direction=${categoryAsc ? "asc" : "desc"}`;
    } else if (stockOrder === "Terceiro") {
      filtros += `&ord3=quantidade_estoque&ord3Direction=${stockAsc ? "asc" : "desc"}`;
    }

    let url = "";

    if (!nameFilter) {
      Modal.confirm({
        title: "Confirmação",
        content:
          "A busca atual pode trazer uma quantidade grande de produtos, deseja alterar a busca?",
        okText: "Sim, cancelar",
        cancelText: "Não, continuar",
        onOk() {
          setLoading(false);
        },
        onCancel() {
          url = `https://techspdv-api-d3e739785e34.herokuapp.com/v1/restaurantes/${loja.id}/produtos/filtroProdutosSemNome/${categoryFilter}/${stockMin}/${stockMax}${filtros}&size=500&page=0`;
          buscarProdutos(url);
        },
      });
    } else {
      url = `https://techspdv-api-d3e739785e34.herokuapp.com/v1/restaurantes/${loja.id}/produtos/filtroProdutos/${nameFilter}/${categoryFilter}/${stockMin}/${stockMax}${filtros}&size=500&page=0`;
      buscarProdutos(url);
    }
  };

  const buscarProdutos = async (url) => {
    if (!accessToken || !idUser) return;
    setLoading(true);
    try {
      const { data } = await axios.get(url, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      });

      const lista = Array.isArray(data?.content) ? data.content : [];
      exportarComDados(lista);
    } catch (error) {
      notification.error({
        message: "Erro ao buscar produtos",
        description: "Ocorreu um erro ao buscar os produtos. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ========= Normalização de um produto vindo do backend ========= */
  const resolverNomeCategoria = (prod) => {
    // tenta pegar o nome da categoria a partir de vários formatos possíveis
    return (
      prod?.categoria?.nome ??
      prod?.nomeCategoria ??
      prod?.categoriaNome ??
      categorias?.find((c) => c?.id === (prod?.idCategoria || prod?.categoria_id))?.nome ??
      prod?.categoria ??
      ""
    );
  };

  const normalizarProduto = (p) => {
    console.log(p);
    return {
      id: p?.id ?? "",
      nome: p?.nome ?? "",
      descricao: p?.descricao ?? p?.descr ?? "",
      preco: parseNumberBR(p?.preco ?? p?.valor ?? ""),
      custo: parseNumberBR(parseFloat(p?.custo) ?? "0"),
      codigo: p?.codigo ?? p?.cod ?? p?.ocidgo ?? "",
      ean: p?.ean ?? "",
      unidade: p?.unidade ?? p?.uni ?? "",
      categoria: resolverNomeCategoria(p), // será impresso como "nome da categoria"
      ncm: p?.ncm ?? "",
      cfop: p?.cfop ?? "",
      origem: p?.origem ?? "",
      cst: p?.cst ?? "",

      // impostos - várias chaves possíveis vindas do backend
      picms: parseNumberBR(p?.picms ?? p?.p_icms ?? p?.aliquota_icms ?? ""),
      predBc: parseNumberBR(p?.predBc ?? p?.p_red_bc ?? p?.reducao_icms ?? ""),
      cstIpi: p?.cstIpi ?? p?.cstIpi ?? "",
      pipi: parseNumberBR(p?.pipi ?? p?.p_ipi ?? p?.aliquota_ipi ?? ""),
      cstPis: p?.cstPis ?? p?.cstPis ?? "",
      ppis: parseNumberBR(p?.ppis ?? p?.p_pis ?? p?.aliquota_pis ?? ""),
      cstCofins: p?.cstCofins ?? p?.cstCofins ?? "",
      pcofins: parseNumberBR(p?.pcofins ?? p?.p_cofins ?? p?.aliquota_cofins ?? ""),
    };
  };

  /* ========= Exportações ========= */
  async function exportarVazia() {
    const ws = montarWorksheetAoA([]); // só cabeçalhos
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Produtos");
    XLSX.writeFile(wb, "PlanilhaProdutos.xlsx");
  }

  async function exportarComDados(produtos) {
    const normalizados = (produtos || []).map(normalizarProduto);
    const ws = montarWorksheetAoA(normalizados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Produtos");
    XLSX.writeFile(wb, "PlanilhaProdutos_exemplo.xlsx");

    if (typeof handleCloseModal === "function") handleCloseModal();
  }

  /* ========= RENDER ========= */
  return (
    <Container ref={containerRef}>
      <FilterCard>
        <Title level={3}>SELEÇÃO DE PRODUTOS</Title>

        {/* Filtro 1: Nome + Ordem + Direção */}
        <FilterRow>
          <Input
            style={{ flex: 1 }}
            placeholder="Nome, Descrição ou Código..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
          <Select
            style={{ width: 120 }}
            value={nameOrder}
            onChange={(val) => setNameOrder(val)}
          >
            <Option value="Nenhum">Nenhum</Option>
            <Option value="Primeiro">Primeiro</Option>
            <Option value="Segundo">Segundo</Option>
            <Option value="Terceiro">Terceiro</Option>
          </Select>
          <BlackSwitch
            checkedChildren="ASC"
            unCheckedChildren="DESC"
            checked={nameAsc}
            onChange={(checked) => setNameAsc(checked)}
          />
        </FilterRow>

        {/* Filtro 2: Categoria + Ordem + Direção */}
        <FilterRow>
          <Select
            style={{ flex: 1 }}
            placeholder="Categoria..."
            value={categoryFilter}
            onChange={(val) => setCategoryFilter(val)}
          >
            <Option value="todas">Todas</Option>
            {Array.isArray(categorias) &&
              categorias.map((categoria) => (
                <Option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </Option>
              ))}
          </Select>

          <Select
            style={{ width: 120 }}
            value={categoryOrder}
            onChange={(val) => setCategoryOrder(val)}
          >
            <Option value="Nenhum">Nenhum</Option>
            <Option value="Primeiro">Primeiro</Option>
            <Option value="Segundo">Segundo</Option>
            <Option value="Terceiro">Terceiro</Option>
          </Select>
          <BlackSwitch
            checkedChildren="ASC"
            unCheckedChildren="DESC"
            checked={categoryAsc}
            onChange={(checked) => setCategoryAsc(checked)}
          />
        </FilterRow>

        {/* Filtro 3: Estoque + Ordem + Direção */}
        <FilterRow>
          <Input
            style={{ flex: 1 }}
            placeholder="Estoque de"
            value={stockMin}
            onChange={(e) => setStockMin(e.target.value)}
          />
          <Input
            style={{ flex: 1 }}
            placeholder="Até"
            value={stockMax}
            onChange={(e) => setStockMax(e.target.value)}
          />
          <Select
            style={{ width: 120 }}
            value={stockOrder}
            onChange={(val) => setStockOrder(val)}
          >
            <Option value="Nenhum">Nenhum</Option>
            <Option value="Primeiro">Primeiro</Option>
            <Option value="Segundo">Segundo</Option>
            <Option value="Terceiro">Terceiro</Option>
          </Select>
          <BlackSwitch
            checkedChildren="ASC"
            unCheckedChildren="DESC"
            checked={stockAsc}
            onChange={(checked) => setStockAsc(checked)}
          />
        </FilterRow>

        <Button
          type="primary"
          block
          onClick={handleContinue}
          disabled={transacaoPendente || executarBusca}
          style={{ backgroundColor: "black", borderColor: "black", color: "#fff" }}
        >
          Exportar Produtos
        </Button>

        <Button
          type="primary"
          block
          onClick={exportarVazia}
          disabled={transacaoPendente || executarBusca}
          style={{
            backgroundColor: "black",
            borderColor: "black",
            color: "#fff",
            marginTop: 10,
          }}
        >
          Exportar Planilha Vazia
        </Button>
      </FilterCard>
    </Container>
  );
};

export default ExportarProdutos;
